import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import type { Announcement } from '../../hooks/useAnnouncements';

type ExpiringInfo = {
  amount: number;
  expires_at: string;
  days_until_expiry: number;
};

type NotificationContentProps = {
  expiringSoon?: ExpiringInfo | null;
  announcements?: Announcement[];
  onLinkClick?: () => void;
  onClose?: () => void; // 明示的に閉じるボタン用
  onMarkAllAsRead?: () => void; // すべて既読にする
  onOpenJobHistory?: () => void; // ジョブ履歴(キュー)を開く。渡されたときのみヘッダー中央にボタン表示(モバイル専用)
};

function formatExpiryDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
  });
}

function formatJobTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'たった今';
  if (diffMins < 60) return `${diffMins}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;
  if (diffDays < 7) return `${diffDays}日前`;

  return date.toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// 統合フィードアイテムの型
type FeedItem =
  | { type: 'announcement'; data: Announcement; timestamp: number };

/**
 * 通知ポップアップの内容コンポーネント
 * PC用NotificationBellとモバイル用MainNavで共有
 */
export function NotificationContent({
  expiringSoon,
  announcements,
  onLinkClick,
  onClose,
  onMarkAllAsRead,
  onOpenJobHistory,
}: NotificationContentProps) {
  const hasExpiringSoon = expiringSoon && expiringSoon.amount > 0;

  // お知らせを時系列でフィード化
  const feedItems = useMemo(() => {
    const items: FeedItem[] = [];

    if (announcements) {
      for (const a of announcements) {
        items.push({
          type: 'announcement',
          data: a,
          timestamp: new Date(a.created_at).getTime(),
        });
      }
    }

    items.sort((a, b) => b.timestamp - a.timestamp);
    return items;
  }, [announcements]);

  const hasFeedItems = feedItems.length > 0;

  return (
    <>
      <div className="notification-popup-header">
        <h3>お知らせ</h3>
        {onOpenJobHistory && (
          <button
            type="button"
            className="notification-header-job-history"
            onClick={onOpenJobHistory}
          >
            生成キュー
          </button>
        )}
        <div className="notification-header-actions">
          {onMarkAllAsRead && (
            <button
              type="button"
              className="notification-mark-read-button"
              onClick={onMarkAllAsRead}
            >
              すべて既読
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="notification-close-button"
              onClick={onClose}
              aria-label="閉じる"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      <div className="notification-popup-content">
        {hasExpiringSoon && (
          <div className="notification-item notification-item-warning">
            <span className="notification-item-icon">
              <AlertTriangle size={16} aria-hidden="true" />
            </span>
            <div className="notification-item-body">
              <p className="notification-item-message">
                <strong>{Math.floor(expiringSoon.amount).toLocaleString()}ポイント</strong>が
                {formatExpiryDate(expiringSoon.expires_at)}に失効します
              </p>
              <p className="notification-item-sub">
                残り{expiringSoon.days_until_expiry}日
              </p>
            </div>
          </div>
        )}

        {/* お知らせフィード（時系列順） */}
        {feedItems.map((item) => {
          const a = item.data;
          return (
            <div
              key={`announcement-${a.id}`}
              className="notification-feed-item"
            >
              <div className="notification-feed-row">
                <div className="notification-feed-meta">
                  <span className="notification-feed-title">{a.message}</span>
                  <span className="notification-feed-time">{formatJobTime(a.created_at)}</span>
                </div>
                <div className="notification-feed-thumbnail notification-feed-thumbnail-icon">
                  <img src="/information_circle.svg" alt="" />
                </div>
              </div>
            </div>
          );
        })}

        {!hasExpiringSoon && !hasFeedItems && (
          <div className="notification-empty">
            <p>新しい通知はありません</p>
          </div>
        )}
      </div>
      {hasExpiringSoon && (
        <div className="notification-popup-footer">
          <Link
            to="/dashboard"
            className="notification-link"
            onClick={onLinkClick}
          >
            詳細を見る
          </Link>
        </div>
      )}
    </>
  );
}
